<script setup lang="ts">
import { useModal } from '~/composables/useModal'
import { AuthStatus } from '~/types/auth'
import { ENGLISH_MODE_CLASS } from '~/models/mapping'
import type { ToasterProps } from '#ui/components/Toaster.vue'
import {
  applyStoredConsentOnLoad,
  getGoogleTagManager,
  revokeConsent
} from '~/utils/cookie.ts'

const authStore = useAuthStore()
const userStore = useUsersStore()
const localeStore = useLocaleStore()
const urlHistoryStore = useUrlHistoryStore()

const { h_e_l_p } = useMagicKeys()

const route = useRoute()

const { isConsentGiven, cookiesEnabled, cookiesEnabledIds } = useCookieControl()

const { setLocale } = useI18n()

useHead({
  templateParams: {
    schemaOrg: {
      host: 'https://thuisgekookt.nl/',
      path: route.path,
      inLanguage: localeStore.locale
    }
  }
})

const { modalIsOpen: errorReportingModalIsOpen, openModal } = useModal()
whenever(h_e_l_p, openModal)

const removeConsentCookieName = () => {
  isConsentGiven.value = false
  cookiesEnabled.value = []

  userStore.visitorId = undefined
  urlHistoryStore.hasMadeCookieDecision = false

  revokeConsent(getGoogleTagManager())
}

const toastProps: ToasterProps = {
  max: 3,
  expand: true,
  ui: {
    viewport: tw`max-lg:mb-8 max-lg:right-2 max-lg:left-2`
  }
}

useHead({
  htmlAttrs: {
    lang: () => localeStore.locale
  }
})

if (import.meta.client) {
  watch(
    () => localeStore.locale,
    (newValue) => {
      const body = document.documentElement

      if (newValue === 'en') {
        body.classList.add(ENGLISH_MODE_CLASS)
      } else {
        body.classList.remove(ENGLISH_MODE_CLASS)
      }
    },
    { immediate: true }
  )

  if (!import.meta.dev) {
    const googleTagManager = getGoogleTagManager()
    applyStoredConsentOnLoad(
      googleTagManager,
      isConsentGiven.value,
      cookiesEnabledIds.value
    )
  }
}

onMounted(() => {
  if ('lang' in route.query) {
    const langQuery = route.query.lang as Locale
    const queryLocale = langQuery === 'nl' || langQuery === 'en' ? langQuery : undefined

    if (queryLocale) {
      setLocale(queryLocale)
      localeStore.locale = queryLocale
      localeStore.hasInitializedLocale = true
    }
  }

  if (localeStore.hasInitializedLocale) {
    setLocale(localeStore.locale)
  } else {
    setLocale('nl')
    localeStore.locale = 'nl'
    localeStore.hasInitializedLocale = true
  }

  if (
    userStore.specificUser &&
    (!authStore.token || authStore.status === AuthStatus.UNAUTHORIZED)
  ) {
    userStore.specificUser = undefined
  }
})
</script>

<template>
  <UApp :toaster="toastProps">
    <NuxtAnnouncer />
    <NuxtRouteAnnouncer />
    <NuxtPage />

    <ClientOnly>
      <CookieBanner />

      <LazyErrorReportingModal
        v-if="errorReportingModalIsOpen"
        v-model:open="errorReportingModalIsOpen"
        @reset-cookies="removeConsentCookieName"
      />
    </ClientOnly>
  </UApp>
</template>
